import { useState } from 'react';
import type React from 'react';
import { Button } from '@/components/ui/button';
import { Copy, Download } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import type { Artifact } from '@/types';

// Utility function to process bold text formatting
const processBoldText = (text: string, keyPrefix: string): React.ReactNode => {
  // Simple approach: split by ** and alternate between normal and bold
  const parts = text.split(/(\*\*.*?\*\*)/g);

  console.log('🔤 Processing bold text:', {
    originalText: text,
    parts,
    keyPrefix
  });

  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          // This is a bold part - remove the ** and make it bold
          const boldText = part.slice(2, -2);
          console.log('✨ Making bold:', boldText);
          return (
            <span key={`${keyPrefix}-bold-${index}`} className="font-bold">
              {boldText}
            </span>
          );
        } else {
          // This is regular text
          return part || null;
        }
      })}
    </>
  );
};

interface TextRendererProps {
  artifact: Artifact;
}

export function TextRenderer({ artifact }: TextRendererProps) {
  const [showRaw, setShowRaw] = useState(false);

  const textContent = typeof artifact.data === 'string'
    ? artifact.data
    : String(artifact.data);

  console.log('📝 TextRenderer - Received artifact:', {
    name: artifact.name,
    mimeType: artifact.mimeType,
    dataType: typeof artifact.data,
    textLength: textContent.length,
    textPreview: textContent.substring(0, 200),
    hasBoldText: textContent.includes('**'),
    hasBullets: /^(\s*)[*•-]\s+/m.test(textContent)
  });

  // Add a visible indicator that TextRenderer is being used
  console.warn('🚨 TextRenderer is being used!', artifact);

  const handleCopy = () => {
    navigator.clipboard.writeText(textContent);
  };

  const handleDownload = () => {
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = artifact.name || 'text-artifact.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!textContent.trim()) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          The text content is empty.
        </AlertDescription>
      </Alert>
    );
  }

  // Format text with markdown-like syntax
  const formatText = (text: string) => {
    if (showRaw) return <pre className="whitespace-pre-wrap break-words">{text}</pre>;

    // Split text into paragraphs and process each one
    return (
      <div className="space-y-4">
        {text.split('\n\n').map((paragraph, pIndex) => {
          const lines = paragraph.split('\n');

          return (
            <div key={`paragraph-${pIndex}`} className="space-y-2">
              {renderParagraphContent(lines, pIndex)}
            </div>
          );
        })}
      </div>
    );
  };

  // Render paragraph content with proper formatting
  const renderParagraphContent = (lines: string[], paragraphIndex: number) => {
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      const trimmedLine = line.trim();

      // Check if this line is a bullet point
      const bulletMatch = line.match(/^(\s*)[*•-]\s+(.*)$/);

      if (bulletMatch) {
        console.log('🔸 Found bullet point:', { line, bulletMatch });
        // Collect all consecutive bullet points
        const listItems: React.ReactNode[] = [];

        while (i < lines.length) {
          const currentLine = lines[i];
          const currentBulletMatch = currentLine.match(/^(\s*)[*•-]\s+(.*)$/);

          if (currentBulletMatch) {
            const [, indent, content] = currentBulletMatch;
            // Count actual spaces (not just divide by 4)
            const spaceCount = indent.length;
            const indentLevel = Math.floor(spaceCount / 4);

            console.log('🔹 Processing bullet:', {
              line: currentLine,
              indent: `"${indent}"`,
              spaceCount,
              indentLevel,
              content
            });

            listItems.push(
              <li
                key={`li-${paragraphIndex}-${i}`}
                className="text-sm leading-relaxed mb-1"
                style={{
                  marginLeft: indentLevel > 0 ? `${indentLevel * 1.5}rem` : '0',
                  listStyleType: indentLevel === 0 ? 'disc' : indentLevel === 1 ? 'circle' : 'square'
                }}
              >
                {processBoldText(content, `li-${paragraphIndex}-${i}`)}
              </li>
            );
            i++;
          } else {
            break;
          }
        }

        if (listItems.length > 0) {
          elements.push(
            <ul key={`list-${paragraphIndex}-${elements.length}`} className="pl-6">
              {listItems}
            </ul>
          );
        }
      } else {
        // Handle regular text lines
        if (trimmedLine) {
          // Preserve leading whitespace for indentation
          const leadingSpaces = line.match(/^(\s*)/)?.[1] || '';
          const indentStyle = leadingSpaces ? {
            paddingLeft: `${leadingSpaces.length * 0.5}rem`
          } : undefined;

          elements.push(
            <div
              key={`line-${paragraphIndex}-${i}`}
              className="text-sm leading-relaxed"
              style={indentStyle}
            >
              {processBoldText(trimmedLine, `line-${paragraphIndex}-${i}`)}
            </div>
          );
        } else {
          // Empty line for spacing
          elements.push(
            <div key={`empty-${paragraphIndex}-${i}`} className="h-2" />
          );
        }
        i++;
      }
    }

    return elements;
  };

  return (
    <div className="space-y-4">
      {/* DEBUG: Visible indicator */}
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-3 py-2 rounded text-xs">
        🔧 DEBUG: TextRenderer is active | MimeType: {artifact.mimeType} | HasBold: {textContent.includes('**') ? 'Yes' : 'No'} | HasBullets: {/^(\s*)[*•-]\s+/m.test(textContent) ? 'Yes' : 'No'}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowRaw(!showRaw)}
        >
          {showRaw ? 'Show Formatted' : 'Show Raw'}
        </Button>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
          >
            <Copy className="h-4 w-4 mr-1" />
            Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
          >
            <Download className="h-4 w-4 mr-1" />
            Download
          </Button>
        </div>
      </div>

      {/* Text Content */}
      <div className="border rounded-lg p-6 bg-background max-h-96 overflow-auto">
        {formatText(textContent)}
      </div>
    </div>
  );
}